import { Card, CardContent } from "../components/ui/card";

/**
 * Public Privacy Policy page — required by the Apple App Store submission
 * and by A2P 10DLC compliance for SMS. Not gated behind auth; accessible at /privacy.
 */
export default function Privacy() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto p-6 sm:p-10">
        <div className="mb-8 flex items-center gap-3">
          <img src="/logo-light.png" alt="Tides Team" className="h-10 w-auto" />
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Privacy Policy</h1>
            <p className="text-xs text-slate-500 mt-1">Last updated: February 17, 2026</p>
          </div>
        </div>

        <Card>
          <CardContent className="prose prose-slate max-w-none pt-6 text-[15px] leading-relaxed">
            <p><b>Tides Team Exteriors LLC</b> ("Tides Team", "we", "us", "our") operates the Tides Hub customer relationship management platform and mobile app. This Privacy Policy explains what information we collect, how we use it, and your rights.</p>

            <h2>Information we collect</h2>
            <p>We collect information you or your customers voluntarily provide through:</p>
            <ul>
              <li><b>Contact and quote forms on tidesteam.com:</b> name, email, phone number, service address, and details about the requested work.</li>
              <li><b>Signed agreements and invoices:</b> billing address, project scope, and payment records.</li>
              <li><b>SMS communications:</b> your phone number and message content, only when you have opted in through one of the methods described in the SMS section below.</li>
              <li><b>App usage:</b> device type, iOS version, and diagnostic information for the mobile app (via Apple TestFlight and Sentry-style crash reporting).</li>
            </ul>

            <h2>How we use your information</h2>
            <ul>
              <li>To provide roofing, siding, gutter, and other exterior contractor services you have requested.</li>
              <li>To send appointment confirmations, crew arrival notifications, project status updates, invoices, receipts, and post-service review requests via email and SMS.</li>
              <li>To process payments through Stripe.</li>
              <li>To improve our services and diagnose technical issues.</li>
            </ul>

            <h2>SMS messaging and consent</h2>
            <p>Recipients opt in to receive SMS messages from Tides Team Exteriors by one of the following methods:</p>
            <ol>
              <li>Submitting the contact form on tidesteam.com which displays SMS consent disclosure language.</li>
              <li>Signing a service agreement that contains SMS consent language.</li>
              <li>Providing verbal consent during an in-person or telephone consultation.</li>
            </ol>
            <p>Recipients may opt out at any time by replying <b>STOP</b>, <b>STOPALL</b>, <b>UNSUBSCRIBE</b>, <b>CANCEL</b>, <b>END</b>, or <b>QUIT</b> to any SMS message from Tides Team Exteriors. Replying <b>HELP</b> returns support contact information. Message and data rates may apply. Message frequency varies with active project activity.</p>
            <p><b>Tides Team Exteriors does not sell or share mobile numbers or SMS opt-in data with third parties for marketing purposes.</b> No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. All the categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.</p>

            <h2>Sharing your information</h2>
            <p>We share information only with service providers strictly necessary to run our business:</p>
            <ul>
              <li><b>Stripe</b> — payment processing.</li>
              <li><b>Twilio</b> — SMS delivery.</li>
              <li><b>Resend</b> — transactional email delivery.</li>
              <li><b>Google (Calendar)</b> — scheduling sync with your explicit connection.</li>
              <li><b>Apple</b> — push notification delivery to the iOS app.</li>
            </ul>
            <p>We do not sell your personal information under any circumstance.</p>

            <h2>Data retention</h2>
            <p>We retain project, invoice, and communication records for as long as needed to service you, comply with tax and legal obligations (typically 7 years), and defend our legal rights. You may request deletion of your data by contacting us at the address below; we will honor deletion requests subject to legal retention requirements.</p>

            <h2>Your rights</h2>
            <p>You may request access to, correction of, or deletion of your personal information at any time by emailing <a href="mailto:operations@tidesteam.com">operations@tidesteam.com</a>.</p>

            <h2>Children</h2>
            <p>Tides Hub is not directed to children under 13, and we do not knowingly collect information from children.</p>

            <h2>Changes to this policy</h2>
            <p>We may update this policy occasionally. Material changes will be reflected in the "Last updated" date above.</p>

            <h2>Contact us</h2>
            <p>
              Tides Team Exteriors LLC<br />
              Charleston, South Carolina<br />
              Email: <a href="mailto:operations@tidesteam.com">operations@tidesteam.com</a><br />
              Phone: (843) 252-4610
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
